import { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { captureError } from "@/lib/logger";
import * as Sentry from "@sentry/react";

interface AuthContextType {
    user: User | null;
    session: Session | null;
    isAuthorized: boolean;
    /** True after checkAuthorization has run for the current user. */
    authResolved: boolean;
    partnerId: number | null;
    isLoading: boolean;
    sendOtp: (email: string) => Promise<{ error: Error | null }>;
    verifyOtp: (email: string, token: string) => Promise<{ error: Error | null }>;
    sendPhoneOtp: (phone: string) => Promise<{ error: Error | null }>;
    verifyPhoneOtp: (phone: string, token: string) => Promise<{ error: Error | null }>;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    // `AuthProvider` sits inside `QueryClientProvider` (src/App.tsx), so the client this
    // returns is the app's one and only cache. `signOut` empties it — see there for why.
    const queryClient = useQueryClient();
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [authResolved, setAuthResolved] = useState(false);
    const [partnerId, setPartnerId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const checkingUserIdRef = useRef<string | null>(null);

    useEffect(() => {
        let isMounted = true;

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (event, session) => {
                if (!isMounted) return;

                setSession(session);
                setUser(session?.user ?? null);

                if (session?.user) {
                    Sentry.setUser({ id: session.user.id, email: session.user.email, phone: session.user.phone });
                } else {
                    Sentry.setUser(null);
                }

                if (event === 'TOKEN_REFRESHED') {
                    return;
                }

                if (event === 'SIGNED_IN' && session?.user?.id === user?.id && isAuthorized) {
                    return;
                }

                if (session?.user) {
                    if (checkingUserIdRef.current === session.user.id) return;
                    setIsLoading(true);
                    setAuthResolved(false);
                    setTimeout(() => {
                        if (isMounted) {
                            checkAuthorization(session.user.id);
                        }
                    }, 0);
                } else {
                    checkingUserIdRef.current = null;
                    setIsAuthorized(false);
                    setAuthResolved(false);
                    setPartnerId(null);
                    setIsLoading(false);
                }
            }
        );

        supabase.auth.getSession().then(({ data: { session }, error }) => {
            if (!isMounted) return;

            if (error) {
                captureError(error);
                supabase.auth.signOut();
                setSession(null);
                setUser(null);
                setIsAuthorized(false);
                setAuthResolved(false);
                setPartnerId(null);
                setIsLoading(false);
                return;
            }

            setSession(session);
            setUser(session?.user ?? null);

            if (session?.user) {
                if (checkingUserIdRef.current === session.user.id) return;
                checkAuthorization(session.user.id);
            } else {
                setIsLoading(false);
            }
        });

        return () => {
            isMounted = false;
            subscription.unsubscribe();
        };
    }, []);

    const checkAuthorization = async (userId: string) => {
        checkingUserIdRef.current = userId;
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('partner_id')
                .eq('user_id', userId)
                .maybeSingle();

            if (error) {
                captureError(error);
                setIsAuthorized(false);
                setPartnerId(null);
            } else if (data?.partner_id) {
                setIsAuthorized(true);
                setPartnerId(data.partner_id);
            } else {
                const { data: linkResult } = await supabase.rpc('link_auth_profile', {
                    p_new_user_id: userId,
                });
                if (linkResult?.linked) {
                    if (linkResult.has_partner) {
                        const { data: profileData } = await supabase
                            .from('profiles')
                            .select('partner_id')
                            .eq('user_id', userId)
                            .maybeSingle();
                        setIsAuthorized(true);
                        setPartnerId(profileData?.partner_id ?? null);
                    } else {
                        setIsAuthorized(false);
                        setPartnerId(null);
                    }
                } else {
                    setIsAuthorized(false);
                    setPartnerId(null);
                }
            }
        } catch (err) {
            captureError(err);
            setIsAuthorized(false);
            setPartnerId(null);
        } finally {
            setAuthResolved(true);
            setIsLoading(false);
        }
    };

    const sendOtp = async (email: string) => {
        try {
            const { data, error: rpcError } = await supabase.rpc('prepare_email_login', { p_email: email });
            if (rpcError) {
                return { error: new Error('Failed to validate email') };
            }
            if (data && !data.exists) {
                return { error: new Error('This email address was not found. Please contact support for help.') };
            }
        } catch {
            return { error: new Error('Failed to validate email') };
        }
        const { error } = await supabase.auth.signInWithOtp({ email });
        return { error: error as Error | null };
    };

    const verifyOtp = async (email: string, token: string) => {
        const { error } = await supabase.auth.verifyOtp({
            email,
            token,
            type: 'email',
        });
        return { error: error as Error | null };
    };

    const sendPhoneOtp = async (phone: string) => {
        try {
            const { data, error: rpcError } = await supabase.rpc('prepare_phone_login', { p_phone: phone });
            if (rpcError) {
                return { error: new Error('Failed to validate phone number') };
            }
            if (data && !data.exists) {
                return { error: new Error('This phone number was not found. Please contact support for help.') };
            }
        } catch {
            return { error: new Error('Failed to validate phone number') };
        }

        const { error } = await supabase.auth.signInWithOtp({
            phone,
        });
        return { error: error as Error | null };
    };

    const verifyPhoneOtp = async (phone: string, token: string) => {
        const { error } = await supabase.auth.verifyOtp({
            phone,
            token,
            type: 'sms',
        });
        return { error: error as Error | null };
    };

    /**
     * ── SIGNING OUT EMPTIES THE QUERY CACHE, AND IT HAS TO ────────────────────
     * Signing out here is an SPA navigation, not a reload: `await signOut()` then
     * `navigate('/auth')`. Nothing unmounts the QueryClient, so without this line every
     * answer the previous partner's session collected sits in memory for react-query's
     * default five-minute gc window — and the next partner to sign in on the same phone
     * renders those rows while their own fetch is still in flight.
     *
     * MOST of this app's queries are keyed on `partnerId`, which makes them miss rather
     * than leak. The agreement queries are not, and cannot usefully be: they read
     * ZERO-ARGUMENT, session-scoped RPCs (`get_my_partner_agreements`,
     * `get_my_pending_signatures`) and document-id-keyed ones (`agreement-document`,
     * `agreement-render`, `agreement-signed-copy`) whose whole access rule lives on the
     * server under `my_partner_id()`. `useSignedCopy` also holds `staleTime: Infinity`, so
     * a hit there would serve another partner's EXECUTED INSTRUMENT with no network call at
     * all.
     *
     * `clear()` rather than a longer key: one line that covers every query this app has and
     * every one it grows, at the one moment where throwing the cache away is exactly right.
     */
    const signOut = async () => {
        checkingUserIdRef.current = null;
        await supabase.auth.signOut();
        Sentry.setUser(null);
        setUser(null);
        setSession(null);
        setIsAuthorized(false);
        setAuthResolved(false);
        setPartnerId(null);
        queryClient.clear();
    };

    const value: AuthContextType = {
        user,
        session,
        isAuthorized,
        authResolved,
        partnerId,
        isLoading,
        sendOtp,
        verifyOtp,
        sendPhoneOtp,
        verifyPhoneOtp,
        signOut,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
